/**
 * useStudentProfilePromptState — les gestes de la relance « Personnalise ton profil », et ses deux
 * écritures : le sport (`authService.updateStudentAccount`) et l'écart d'un an
 * (`authService.dismissProfileCustomization`, par la croix, le voile, Échap, « Plus tard » et
 * « Générer mon avatar »).
 *
 * Port de `student/account/ProfileCustomizationPromptModal.js`, geste pour geste, et sous les noms
 * d'événements de l'ancienne (`shown` part du déclencheur de la coquille). Ce qui en diffère :
 *  - le sport part avec le reste du compte et sans la classe (`toSportChoicePayload`) ;
 *  - un refus d'un autre champ du compte renvoie aux paramètres (`refusesOtherThanSport`) ;
 *  - `sport_saved` dit le sport enregistré, aussi quand l'élève a confirmé celui de son profil sans
 *    y toucher (l'ancienne envoyait alors `null`).
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useNavigation } from "@react-navigation/native"
import { useEffect, useReducer } from "react"
import { useSelector } from "react-redux"

import { authService } from "../../../../../../data/services/authService"
import {
    refusesOtherThanSport,
    SPORT_OPTIONS,
    toProfileSettings,
    toSportChoicePayload,
    type ApiStudentSettingsUser,
} from "../../../../adapters/student/Account/studentSettingsAdapter"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { initialStudentProfilePromptUiState, studentProfilePromptUiReducer } from "./studentProfilePromptUiState"
import { selectAccount } from "../../../../view-models/shared/displayedStudent"

type StackNavigation = { push: (route: string, params?: object) => void }

/** L'accusé reste à l'écran le temps de l'ancienne modale avant que l'avatar se propose. */
export const PROFILE_PROMPT_SAVED_MS = 1200

const SAVE_FAILED = "Ton sport n'a pas pu être enregistré. Réessaie dans un instant."
const OTHER_FIELD_REFUSED =
    "Ton sport n'a pas pu être enregistré : une autre information de ton compte est à corriger dans tes paramètres."

/** `onDismiss` : la relance se referme, la coquille la cache et relit le compte. */
export function useStudentProfilePromptState(onDismiss: () => void) {
    const navigation = useNavigation() as unknown as StackNavigation
    const user = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => selectAccount(state) as ApiStudentSettingsUser | undefined
    )
    const [ui, dispatchUi] = useReducer(studentProfilePromptUiReducer, initialStudentProfilePromptUiState)

    const profileSport = user ? toProfileSettings(user).sportId : ""
    const selectedSport = ui.touchedSport ?? (profileSport !== "" ? profileSport : null)

    const save = useApiMutation({
        service: authService.updateStudentAccount,
        voice: "student",
        fallback: SAVE_FAILED,
        onSuccess: () => {
            amplitude.track("profile_customization_prompt.sport_saved", { sport: selectedSport })
            dispatchUi({ type: "SAVED" })
        },
        onError: (message, error) =>
            dispatchUi({ type: "FAILED", message: refusesOtherThanSport(error) ? OTHER_FIELD_REFUSED : message }),
    })
    // Un refus ne se dit pas : la relance est déjà fermée, et revient à la prochaine session.
    const dismissal = useApiMutation({ service: authService.dismissProfileCustomization, voice: "student" })

    useEffect(() => {
        if (ui.step !== "saved") return
        const timer = setTimeout(() => dispatchUi({ type: "OFFER_AVATAR" }), PROFILE_PROMPT_SAVED_MS)
        return () => clearTimeout(timer)
    }, [ui.step])

    const dismiss = () => {
        dismissal.start()
        onDismiss()
    }

    return {
        step: ui.step,
        sports: SPORT_OPTIONS,
        selectedSportId: selectedSport,
        saving: ui.saving,
        error: ui.error,
        onSelectSport: (sport: string) => {
            amplitude.track("profile_customization_prompt.sport_click", { sport })
            dispatchUi({ type: "TOUCH", sport })
        },
        onConfirmSport: () => {
            if (user == null || selectedSport == null || ui.saving) return
            amplitude.track("profile_customization_prompt.sport_confirm", { sport: selectedSport })
            dispatchUi({ type: "SEND", sport: selectedSport })
            save.start({}, toSportChoicePayload(user, selectedSport))
        },
        onClose: () => {
            amplitude.track("profile_customization_prompt.dismiss")
            dismiss()
        },
        onGenerateAvatar: () => {
            amplitude.track("profile_customization_prompt.avatar_click")
            dismiss()
            navigation.push("account", { openAIAvatarModal: true })
        },
    }
}
